import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, Mail, MessageSquare, BarChart3, Search, Trash2, Eye, 
  ChevronLeft, ChevronRight, RefreshCw, Download, X, Lock, LogOut,
  Briefcase, GraduationCap, FileDown, Loader2, Calendar, Camera, Send,
  Plus, Edit2, MapPin, Clock, Palette, Check, Vote
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ConferenceNotifier } from '../components/ConferenceNotifier';
import { OutreachComposer } from '../components/OutreachComposer';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'dc_men_admin_auth';

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [conferenceMembers, setConferenceMembers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [events, setEvents] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [pagination, setPagination] = useState({ skip: 0, limit: 10, total: 0 });
  const [exporting, setExporting] = useState(false);
  
  // Event form state
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({
    title: '', description: '', eventDate: '', startTime: '', endTime: '',
    location: '', locationAddress: '', isVirtual: false, virtualLink: '',
    maxAttendees: '', rsvpEnabled: true
  });
  
  // Bulk email state
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [bulkSending, setBulkSending] = useState(false);
  const [pendingAccountsCount, setPendingAccountsCount] = useState(0);
  const [setupAccountSending, setSetupAccountSending] = useState(null);
  
  // Template preview state
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  
  // Admin password reset state
  const [resetPasswordResult, setResetPasswordResult] = useState(null);
  const [resettingPassword, setResettingPassword] = useState(false);
  
  // Conference survey state
  const [surveyResults, setSurveyResults] = useState(null);

  // Conference registration state
  const [confRegData, setConfRegData] = useState(null);
  const [confRegSearch, setConfRegSearch] = useState('');
  const [confRegSelected, setConfRegSelected] = useState(null);
  const [confRegExporting, setConfRegExporting] = useState(false);

  // Member Invitation state
  const [invitationRecipientsText, setInvitationRecipientsText] = useState('');
  const [invitationSending, setInvitationSending] = useState(false);
  const [invitationData, setInvitationData] = useState(null);
  const [invitationPreviewOpen, setInvitationPreviewOpen] = useState(false);
  const [invitationPreviewHtml, setInvitationPreviewHtml] = useState('');
  const [invitationPreviewSubject, setInvitationPreviewSubject] = useState('');
  const [invitationPreviewLoading, setInvitationPreviewLoading] = useState(false);

  // Check if user is already authenticated
  useEffect(() => {
    const checkAuth = async () => {
      const storedPassword = sessionStorage.getItem(STORAGE_KEY);
      if (storedPassword) {
        try {
          const response = await fetch(`${API_URL}/api/admin/verify?password=${encodeURIComponent(storedPassword)}`);
          const data = await response.json();
          if (data.valid) {
            setIsAuthenticated(true);
          } else {
            sessionStorage.removeItem(STORAGE_KEY);
          }
        } catch (error) {
          console.error('Auth check failed:', error);
        }
      }
      setCheckingAuth(false);
    };
    checkAuth();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('password', password);
      
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        sessionStorage.setItem(STORAGE_KEY, password);
        setIsAuthenticated(true);
        toast.success('Welcome to the Admin Dashboard');
      } else {
        toast.error('Invalid password');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Login failed. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setIsAuthenticated(false);
    setPassword('');
    toast.success('Logged out successfully');
  };

  const getStoredPassword = () => sessionStorage.getItem(STORAGE_KEY);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/stats`);
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load statistics');
    }
  }, []);

  const fetchMembers = useCallback(async () => {
    try {
      const url = new URL(`${API_URL}/api/admin/members`);
      url.searchParams.set('skip', pagination.skip);
      url.searchParams.set('limit', pagination.limit);
      if (searchTerm) url.searchParams.set('search', searchTerm);
      
      const response = await fetch(url);
      const data = await response.json();
      setMembers(data.members);
      setPagination(prev => ({ ...prev, total: data.total }));
    } catch (error) {
      console.error('Error fetching members:', error);
      toast.error('Failed to load members');
    }
  }, [pagination.skip, pagination.limit, searchTerm]);

  const fetchContacts = useCallback(async () => {
    try {
      const url = new URL(`${API_URL}/api/admin/contacts`);
      url.searchParams.set('skip', pagination.skip);
      url.searchParams.set('limit', pagination.limit);
      if (searchTerm) url.searchParams.set('search', searchTerm);
      
      const response = await fetch(url);
      const data = await response.json();
      setContacts(data.contacts);
      setPagination(prev => ({ ...prev, total: data.total }));
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Failed to load contacts');
    }
  }, [pagination.skip, pagination.limit, searchTerm]);

  const fetchSubscribers = useCallback(async () => {
    try {
      const url = new URL(`${API_URL}/api/admin/newsletter`);
      url.searchParams.set('skip', pagination.skip);
      url.searchParams.set('limit', pagination.limit);
      if (searchTerm) url.searchParams.set('search', searchTerm);
      
      const response = await fetch(url);
      const data = await response.json();
      setSubscribers(data.subscribers);
      setPagination(prev => ({ ...prev, total: data.total }));
    } catch (error) {
      console.error('Error fetching subscribers:', error);
      toast.error('Failed to load subscribers');
    }
  }, [pagination.skip, pagination.limit, searchTerm]);

  const fetchConferenceMembers = useCallback(async () => {
    try {
      const url = new URL(`${API_URL}/api/admin/members`);
      url.searchParams.set('skip', pagination.skip);
      url.searchParams.set('limit', pagination.limit);
      url.searchParams.set('conference_support', 'true');
      if (searchTerm) url.searchParams.set('search', searchTerm);
      
      const response = await fetch(url);
      const data = await response.json();
      setConferenceMembers(data.members);
      setPagination(prev => ({ ...prev, total: data.total }));
    } catch (error) {
      console.error('Error fetching conference supporters:', error);
      toast.error('Failed to load conference supporters');
    }
  }, [pagination.skip, pagination.limit, searchTerm]);

  const fetchEvents = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/events?upcoming_only=false`);
      const data = await response.json();
      setEvents(data.events || []);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load events');
    }
  }, []);

  const fetchEmailTemplates = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/email-templates`);
      const data = await response.json();
      setEmailTemplates(data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  }, []);

  // Fetch pending accounts count
  const fetchPendingAccountsCount = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/members/pending-accounts`);
      const data = await response.json();
      setPendingAccountsCount(data.pending_count || 0);
    } catch (error) {
      console.error('Error fetching pending accounts:', error);
    }
  }, []);

  // Fetch survey results
  const fetchSurveyResults = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/conference-survey`);
      if (response.ok) {
        const data = await response.json();
        setSurveyResults(data);
      }
    } catch (error) {
      console.error('Failed to fetch survey results:', error);
    }
  }, []);

  // Fetch conference registrations
  const fetchConfRegistrations = useCallback(async () => {
    try {
      const storedPassword = sessionStorage.getItem(STORAGE_KEY);
      if (!storedPassword) return;
      const response = await fetch(
        `${API_URL}/api/admin/conference-registrations?password=${encodeURIComponent(storedPassword)}`
      );
      if (response.ok) {
        const data = await response.json();
        setConfRegData(data);
      }
    } catch (error) {
      console.error('Failed to fetch conference registrations:', error);
    }
  }, []);

  const handleExportConfRegistrations = async () => {
    setConfRegExporting(true);
    try {
      const storedPassword = sessionStorage.getItem(STORAGE_KEY) || '';
      const response = await fetch(
        `${API_URL}/api/admin/conference-registrations/export?password=${encodeURIComponent(storedPassword)}`
      );
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.download = `conference_registrations_${today}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Registrations exported');
    } catch (error) {
      toast.error('Failed to export registrations');
    } finally {
      setConfRegExporting(false);
    }
  };

  const handleDeleteConfRegistration = async (id) => {
    if (!window.confirm('Delete this registration?')) return;
    try {
      const storedPassword = sessionStorage.getItem(STORAGE_KEY) || '';
      const response = await fetch(
        `${API_URL}/api/admin/conference-registrations/${id}?password=${encodeURIComponent(storedPassword)}`,
        { method: 'DELETE' }
      );
      if (!response.ok) throw new Error('Delete failed');
      toast.success('Registration deleted');
      setConfRegSelected(null);
      fetchConfRegistrations();
    } catch (error) {
      toast.error('Failed to delete registration');
    }
  };

  const [confRegResendingId, setConfRegResendingId] = useState(null);
  const [confRegBulkSending, setConfRegBulkSending] = useState(false);

  const handleResendConfRegistration = async (id) => {
    setConfRegResendingId(id);
    try {
      const storedPassword = sessionStorage.getItem(STORAGE_KEY) || '';
      const res = await fetch(
        `${API_URL}/api/admin/conference-registrations/${id}/resend-confirmation?password=${encodeURIComponent(storedPassword)}`,
        { method: 'POST' }
      );
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Resend failed');
      if (d.success) toast.success(`Confirmation email resent to ${d.email}`);
      else {
        const reason = d.error ? `Email failed for ${d.email}: ${d.error}` : `Email send failed for ${d.email}. Check SendGrid domain verification.`;
        toast.error(reason);
      }
      fetchConfRegistrations();
    } catch (e) {
      toast.error(e.message || 'Failed to resend');
    } finally {
      setConfRegResendingId(null);
    }
  };

  const handleBulkResendConfRegistration = async (onlyUnsent) => {
    const scopeLabel = onlyUnsent ? 'only registrants without confirmation yet' : 'ALL registrants';
    if (!window.confirm(`Resend the confirmation email to ${scopeLabel}?`)) return;
    setConfRegBulkSending(true);
    try {
      const storedPassword = sessionStorage.getItem(STORAGE_KEY) || '';
      const res = await fetch(
        `${API_URL}/api/admin/conference-registrations/bulk-resend-confirmation?password=${encodeURIComponent(storedPassword)}&only_unsent=${onlyUnsent}`,
        { method: 'POST' }
      );
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Bulk resend failed');
      toast.success(`Sent ${d.sent} of ${d.total}${d.failed ? ` · ${d.failed} failed` : ''}`);
      if (d.failed && d.error) {
        toast.error(`Reason for failures: ${d.error}`);
      }
      fetchConfRegistrations();
    } catch (e) {
      toast.error(e.message || 'Failed to send');
    } finally {
      setConfRegBulkSending(false);
    }
  };

  // Helper to get the stored admin password
  const getStoredPasswordSafe = () => sessionStorage.getItem(STORAGE_KEY) || '';

  // Fetch invitation recipients
  const fetchInvitationData = useCallback(async () => {
    try {
      const storedPassword = sessionStorage.getItem(STORAGE_KEY);
      if (!storedPassword) return;
      const response = await fetch(
        `${API_URL}/api/admin/member-invitation/recipients?password=${encodeURIComponent(storedPassword)}`
      );
      if (response.ok) {
        const data = await response.json();
        setInvitationData(data);
      }
    } catch (error) {
      console.error('Failed to fetch invitation recipients:', error);
    }
  }, []);

  const handleOpenInvitationPreview = async () => {
    setInvitationPreviewLoading(true);
    try {
      const storedPassword = getStoredPasswordSafe();
      const response = await fetch(
        `${API_URL}/api/admin/member-invitation/preview?password=${encodeURIComponent(storedPassword)}`
      );
      if (!response.ok) throw new Error('Preview failed');
      const data = await response.json();
      setInvitationPreviewSubject(data.subject);
      setInvitationPreviewHtml(data.html);
      setInvitationPreviewOpen(true);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load preview');
    } finally {
      setInvitationPreviewLoading(false);
    }
  };

  const parseRecipientsText = (text) => {
    // Each non-empty line: email[,firstName[,lastName]]
    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    const recipients = [];
    const seen = new Set();
    const skipped = [];
    for (const line of lines) {
      // Also support comma-separated emails on a single line (just emails)
      const parts = line.split(',').map((p) => p.trim()).filter(Boolean);
      if (parts.length === 0) continue;

      // If a single line has multiple bare emails (more than one looks-like-email part),
      // treat each as its own recipient
      const looksLikeEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
      const emailParts = parts.filter(looksLikeEmail);
      if (emailParts.length > 1 && parts.length === emailParts.length) {
        for (const e of emailParts) {
          const k = e.toLowerCase();
          if (seen.has(k)) continue;
          seen.add(k);
          recipients.push({ email: e });
        }
        continue;
      }

      const [email, first_name = '', last_name = ''] = parts;
      if (!looksLikeEmail(email)) {
        skipped.push(line);
        continue;
      }
      const k = email.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      recipients.push({
        email,
        first_name: first_name || undefined,
        last_name: last_name || undefined,
      });
    }
    return { recipients, skipped };
  };

  const handleSendInvitations = async () => {
    const { recipients, skipped } = parseRecipientsText(invitationRecipientsText);

    if (recipients.length === 0) {
      toast.error('No valid email addresses found');
      return;
    }
    if (skipped.length > 0) {
      toast.warning(`${skipped.length} line(s) skipped (invalid email format)`);
    }
    if (
      !window.confirm(
        `Send the member-profile invitation letter to ${recipients.length} recipient(s)?`
      )
    ) {
      return;
    }

    setInvitationSending(true);
    try {
      const storedPassword = getStoredPasswordSafe();
      const response = await fetch(`${API_URL}/api/admin/member-invitation/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: storedPassword, recipients }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Send failed');
      }
      toast.success(`Sent ${data.sent} invitation(s)${data.failed ? `, ${data.failed} failed` : ''}`);
      setInvitationRecipientsText('');
      fetchInvitationData();
    } catch (error) {
      console.error('Send invitations error:', error);
      toast.error(error.message || 'Failed to send invitations');
    } finally {
      setInvitationSending(false);
    }
  };

  const handleDeleteInvitationRecord = async (recipientId) => {
    if (!window.confirm('Delete this tracking record?')) return;
    try {
      const storedPassword = getStoredPasswordSafe();
      const response = await fetch(
        `${API_URL}/api/admin/member-invitation/recipients/${recipientId}?password=${encodeURIComponent(storedPassword)}`,
        { method: 'DELETE' }
      );
      if (!response.ok) throw new Error('Delete failed');
      toast.success('Record deleted');
      fetchInvitationData();
    } catch (error) {
      toast.error('Failed to delete record');
      console.error(error);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    
    const loadData = async () => {
      setLoading(true);
      await fetchStats();
      setLoading(false);
    };
    loadData();
  }, [fetchStats, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    
    if (activeTab === 'members') {
      fetchMembers();
      fetchPendingAccountsCount();
    }
    else if (activeTab === 'contacts') fetchContacts();
    else if (activeTab === 'newsletter') fetchSubscribers();
    else if (activeTab === 'conference') fetchConferenceMembers();
    else if (activeTab === 'events') fetchEvents();
    else if (activeTab === 'email-templates') fetchEmailTemplates();
    else if (activeTab === 'theme-survey') fetchSurveyResults();
    else if (activeTab === 'member-invitations') fetchInvitationData();
    else if (activeTab === 'conference-registrations') fetchConfRegistrations();
  }, [activeTab, fetchMembers, fetchContacts, fetchSubscribers, fetchConferenceMembers, fetchEvents, fetchEmailTemplates, fetchPendingAccountsCount, fetchSurveyResults, fetchInvitationData, fetchConfRegistrations, isAuthenticated]);

  const handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/${type}/${id}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        toast.success('Item deleted successfully');
        if (type === 'members') fetchMembers();
        else if (type === 'contacts') fetchContacts();
        else if (type === 'newsletter') fetchSubscribers();
        fetchStats();
      } else {
        toast.error('Failed to delete item');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete item');
    }
  };

  const handleExportMembers = async () => {
    setExporting(true);
    try {
      const storedPassword = getStoredPassword();
      const response = await fetch(`${API_URL}/api/admin/members/export?password=${encodeURIComponent(storedPassword)}`);
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'dc_men_ece_members.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Members exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export members');
    } finally {
      setExporting(false);
    }
  };

  const [exportingSurveyPdf, setExportingSurveyPdf] = useState(false);

  const handleExportSurveyPdf = async () => {
    setExportingSurveyPdf(true);
    try {
      const storedPassword = getStoredPassword();
      const response = await fetch(`${API_URL}/api/admin/conference-survey/export-pdf?password=${encodeURIComponent(storedPassword)}`);

      if (!response.ok) {
        throw new Error('PDF export failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.download = `conference_theme_survey_${today}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Survey PDF downloaded');
    } catch (error) {
      console.error('Survey PDF export error:', error);
      toast.error('Failed to download survey PDF');
    } finally {
      setExportingSurveyPdf(false);
    }
  };

  const handleExportConferenceSupporters = async () => {
    setExporting(true);
    try {
      const storedPassword = getStoredPassword();
      const response = await fetch(
        `${API_URL}/api/admin/members/export?password=${encodeURIComponent(storedPassword)}&conference_support=true`
      );

      if (!response.ok) {
        if (response.status === 404) {
          toast.info('No conference supporters to export yet');
          return;
        }
        throw new Error('Export failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'dc_men_ece_conference_supporters.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Conference supporters exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export conference supporters');
    } finally {
      setExporting(false);
    }
  };

  const [sendingEmail, setSendingEmail] = useState(null);

  const handleSendWelcomeEmail = async (memberId, memberEmail) => {
    setSendingEmail(memberId);
    try {
      const response = await fetch(`${API_URL}/api/admin/members/${memberId}/send-welcome-email`, {
        method: 'POST'
      });
      
      if (response.ok) {
        toast.success(`Welcome email sent to ${memberEmail}`);
        // Refresh member data to update the welcome_email_sent status
        fetchMembers();
        if (selectedMember && selectedMember.id === memberId) {
          // Update the selected member to show the email was sent
          setSelectedMember(prev => ({ ...prev, welcome_email_sent: true }));
        }
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to send welcome email');
      }
    } catch (error) {
      console.error('Send email error:', error);
      toast.error('Failed to send welcome email');
    } finally {
      setSendingEmail(null);
    }
  };

  // Bulk email handlers
  const handleSelectMember = (memberId, isSelected) => {
    if (isSelected) {
      setSelectedMembers(prev => [...prev, memberId]);
    } else {
      setSelectedMembers(prev => prev.filter(id => id !== memberId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      // Select members who need account setup (no password_hash)
      const membersNeedingSetup = members.filter(m => !m.password_hash).map(m => m.id);
      setSelectedMembers(membersNeedingSetup);
    } else {
      setSelectedMembers([]);
    }
  };

  const handleBulkSendEmails = async (sendAll = false) => {
    setBulkSending(true);
    try {
      const formData = new FormData();
      if (sendAll) {
        formData.append('send_all', 'true');
      } else {
        formData.append('member_ids', JSON.stringify(selectedMembers));
      }
      
      const response = await fetch(`${API_URL}/api/admin/members/bulk-welcome-email`, {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        setSelectedMembers([]);
        fetchMembers();
      } else {
        toast.error(data.detail || 'Failed to send bulk emails');
      }
    } catch (error) {
      console.error('Bulk email error:', error);
      toast.error('Failed to send bulk emails');
    } finally {
      setBulkSending(false);
    }
  };

  // Send account setup email to a single member
  const handleSendAccountSetup = async (memberId, memberEmail) => {
    setSetupAccountSending(memberId);
    try {
      const response = await fetch(`${API_URL}/api/admin/members/${memberId}/setup-account`, {
        method: 'POST'
      });
      
      if (response.ok) {
        toast.success(`Account setup email sent to ${memberEmail}`);
        fetchMembers();
        fetchPendingAccountsCount();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to send account setup email');
      }
    } catch (error) {
      console.error('Send account setup error:', error);
      toast.error('Failed to send account setup email');
    } finally {
      setSetupAccountSending(null);
    }
  };

  // Bulk send account setup emails
  const handleBulkSetupAccounts = async (setupAll = false) => {
    setBulkSending(true);
    try {
      const formData = new FormData();
      if (setupAll) {
        formData.append('setup_all', 'true');
      } else {
        const membersWithoutAccounts = selectedMembers.filter(id => {
          const member = members.find(m => m.id === id);
          return member && !member.password_hash;
        });
        formData.append('member_ids', JSON.stringify(membersWithoutAccounts));
      }
      
      const response = await fetch(`${API_URL}/api/admin/members/bulk-setup-accounts`, {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        setSelectedMembers([]);
        fetchMembers();
        fetchPendingAccountsCount();
      } else {
        toast.error(data.detail || 'Failed to setup accounts');
      }
    } catch (error) {
      console.error('Bulk setup error:', error);
      toast.error('Failed to setup accounts');
    } finally {
      setBulkSending(false);
    }
  };

  // Admin manual password reset
  const handleAdminResetPassword = async (memberId) => {
    if (!window.confirm('This will generate a new temporary password for this member. Continue?')) {
      return;
    }
    
    setResettingPassword(true);
    setResetPasswordResult(null);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/members/${memberId}/reset-password`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setResetPasswordResult(data);
        toast.success('Password reset successfully!');
        fetchMembers();
      } else {
        toast.error(data.detail || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      toast.error('Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  // Event handlers
  const handleCreateEvent = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      Object.entries(eventForm).forEach(([key, value]) => {
        if (value !== '' && value !== null) {
          formData.append(key, value);
        }
      });
      
      const url = editingEvent 
        ? `${API_URL}/api/admin/events/${editingEvent.id}`
        : `${API_URL}/api/admin/events`;
      
      const response = await fetch(url, {
        method: editingEvent ? 'PUT' : 'POST',
        body: formData
      });
      
      if (response.ok) {
        toast.success(editingEvent ? 'Event updated' : 'Event created');
        setShowEventForm(false);
        setEditingEvent(null);
        setEventForm({
          title: '', description: '', eventDate: '', startTime: '', endTime: '',
          location: '', locationAddress: '', isVirtual: false, virtualLink: '',
          maxAttendees: '', rsvpEnabled: true
        });
        fetchEvents();
      } else {
        toast.error('Failed to save event');
      }
    } catch (error) {
      console.error('Event save error:', error);
      toast.error('Failed to save event');
    }
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setEventForm({
      title: event.title || '',
      description: event.description || '',
      eventDate: event.event_date || '',
      startTime: event.start_time || '',
      endTime: event.end_time || '',
      location: event.location || '',
      locationAddress: event.location_address || '',
      isVirtual: event.is_virtual || false,
      virtualLink: event.virtual_link || '',
      maxAttendees: event.max_attendees || '',
      rsvpEnabled: event.rsvp_enabled !== false
    });
    setShowEventForm(true);
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/events/${eventId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        toast.success('Event deleted');
        fetchEvents();
      } else {
        toast.error('Failed to delete event');
      }
    } catch (error) {
      console.error('Delete event error:', error);
      toast.error('Failed to delete event');
    }
  };

  // Email template handler
  const handleActivateTemplate = async (templateKey) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/email-templates/${templateKey}/activate`, {
        method: 'POST'
      });
      
      if (response.ok) {
        toast.success('Template activated');
        fetchEmailTemplates();
      } else {
        toast.error('Failed to activate template');
      }
    } catch (error) {
      console.error('Activate template error:', error);
      toast.error('Failed to activate template');
    }
  };

  // Email template preview handler
  const handlePreviewTemplate = async (template) => {
    setPreviewTemplate(template);
    setPreviewLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/email-templates/${template.key}/preview?member_name=John%20Doe`);
      const data = await response.json();
      if (response.ok) {
        setPreviewHtml(data.html);
      } else {
        toast.error('Failed to load preview');
        setPreviewTemplate(null);
      }
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Failed to load preview');
      setPreviewTemplate(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Account setup email preview handler
  const handlePreviewAccountSetupTemplate = async () => {
    setPreviewTemplate({
      key: 'account_setup',
      name: 'Account Setup Email',
      description: 'Email sent to existing members with temporary credentials',
      preview_color: '#2e7d32',
      is_active: true
    });
    setPreviewLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/email-templates/account-setup/preview?member_name=John%20Doe&member_email=john@example.com`);
      const data = await response.json();
      if (response.ok) {
        setPreviewHtml(data.html);
      } else {
        toast.error('Failed to load preview');
        setPreviewTemplate(null);
      }
    } catch (error) {
      console.error('Account setup preview error:', error);
      toast.error('Failed to load preview');
      setPreviewTemplate(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, skip: 0 }));
    if (activeTab === 'members') fetchMembers();
    else if (activeTab === 'contacts') fetchContacts();
    else if (activeTab === 'newsletter') fetchSubscribers();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'members', label: 'Members', icon: Users },
    { id: 'member-invitations', label: 'Member Invitations', icon: Send },
    { id: 'conference', label: 'Conference Supporters', icon: Calendar },
    { id: 'conference-registrations', label: 'Conference Registrations', icon: FileDown },
    { id: 'email-outreach', label: 'Email Outreach', icon: Mail },
    { id: 'theme-survey', label: 'Theme Survey', icon: Vote },
    { id: 'events', label: 'Events', icon: Calendar },
    { id: 'contacts', label: 'Contacts', icon: MessageSquare },
    { id: 'newsletter', label: 'Newsletter', icon: Mail },
    { id: 'email-templates', label: 'Email Templates', icon: Palette },
  ];

  // Loading state while checking authentication
  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-brand-navy mx-auto mb-4" />
          <p className="text-navy-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-gold-50/30 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-2xl">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-brand-navy rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-navy-900">Admin Access</CardTitle>
            <p className="text-navy-500 mt-2">Enter your password to access the dashboard</p>
          </CardHeader>
          <CardContent className="pt-4">
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="password" className="text-navy-700">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="mt-2"
                  data-testid="admin-password-input"
                  autoFocus
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-brand-navy hover:bg-navy-800 text-white"
                disabled={loginLoading || !password}
                data-testid="admin-login-button"
              >
                {loginLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4 mr-2" />
                    Access Dashboard
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Authenticated Dashboard
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-brand-navy text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold">Admin Dashboard</h1>
              <p className="text-navy-200 mt-2">Manage members, contacts, and newsletter subscribers</p>
            </div>
            <Button 
              variant="outline" 
              onClick={handleLogout}
              className="border-white/30 text-white hover:bg-white/10"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 bg-white p-2 rounded-xl shadow-sm">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setPagination({ skip: 0, limit: 10, total: 0 });
                  setSearchTerm('');
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-brand-navy text-white'
                    : 'text-navy-600 hover:bg-navy-50'
                }`}
                data-testid={`admin-tab-${tab.id}`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Stats Cards */}
            <div className="grid md:grid-cols-4 gap-6">
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-navy-500 text-sm font-medium">Total Members</p>
                      <p className="text-3xl font-bold text-navy-900 mt-1">
                        {loading ? '...' : stats?.stats?.total_members || 0}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-brand-green/10 rounded-xl flex items-center justify-center">
                      <Users className="w-6 h-6 text-brand-green" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-navy-500 text-sm font-medium">Conference Supporters</p>
                      <p className="text-3xl font-bold text-navy-900 mt-1">
                        {loading ? '...' : stats?.stats?.conference_supporters || 0}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-brand-gold/10 rounded-xl flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-brand-gold" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-navy-500 text-sm font-medium">Contact Messages</p>
                      <p className="text-3xl font-bold text-navy-900 mt-1">
                        {loading ? '...' : stats?.stats?.total_contacts || 0}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-brand-navy/10 rounded-xl flex items-center justify-center">
                      <MessageSquare className="w-6 h-6 text-brand-navy" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-navy-500 text-sm font-medium">Newsletter Subscribers</p>
                      <p className="text-3xl font-bold text-navy-900 mt-1">
                        {loading ? '...' : stats?.stats?.total_subscribers || 0}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-brand-red/10 rounded-xl flex items-center justify-center">
                      <Mail className="w-6 h-6 text-brand-red" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Activity */}
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="w-5 h-5 text-brand-green" />
                    Recent Members
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {stats?.recent_members?.length > 0 ? (
                    <div className="space-y-3">
                      {stats.recent_members.map((member, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium text-navy-900">{member.full_name}</p>
                            <p className="text-sm text-navy-500">{member.email}</p>
                          </div>
                          <span className="text-xs text-navy-400">{formatDate(member.created_at)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-navy-500 text-center py-4">No recent members</p>
                  )}
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-brand-gold" />
                    Recent Contacts
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {stats?.recent_contacts?.length > 0 ? (
                    <div className="space-y-3">
                      {stats.recent_contacts.map((contact, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg group">
                          <div className="flex-1">
                            <p className="font-medium text-navy-900">{contact.name}</p>
                            <p className="text-sm text-navy-500">{contact.subject || 'No subject'}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-navy-400">{formatDate(contact.created_at)}</span>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 hover:bg-red-50 transition-opacity"
                              onClick={() => handleDelete('contacts', contact.id)}
                              data-testid={`delete-recent-contact-${contact.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-navy-500 text-center py-4">No recent contacts</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Members Tab */}
        {activeTab === 'members' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <form onSubmit={handleSearch} className="flex gap-2 w-full sm:w-auto">
                <Input
                  placeholder="Search members..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full sm:w-64"
                  data-testid="admin-search-members"
                />
                <Button type="submit" variant="outline">
                  <Search className="w-4 h-4" />
                </Button>
                <Button type="button" variant="outline" onClick={() => { setSearchTerm(''); fetchMembers(); }}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </form>
              <div className="flex items-center gap-4">
                <Button 
                  variant="outline" 
                  onClick={handleExportMembers}
                  disabled={exporting}
                  className="text-brand-green border-brand-green hover:bg-brand-green hover:text-white"
                  data-testid="admin-export-members"
                >
                  {exporting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <FileDown className="w-4 h-4 mr-2" />
                  )}
                  Export CSV
                </Button>
                {selectedMembers.length > 0 && (
                  <Button
                    onClick={() => handleBulkSetupAccounts(false)}
                    disabled={bulkSending}
                    className="bg-brand-gold hover:bg-gold-600 text-navy-900"
                    data-testid="bulk-send-selected"
                  >
                    {bulkSending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                    Setup Selected ({selectedMembers.length})
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => handleBulkSendEmails(true)}
                  disabled={bulkSending}
                  className="text-brand-gold border-brand-gold"
                  data-testid="bulk-send-all"
                >
                  {bulkSending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                  Send All Pending
                </Button>
                {pendingAccountsCount > 0 && (
                  <Button
                    variant="outline"
                    onClick={() => handleBulkSetupAccounts(true)}
                    disabled={bulkSending}
                    className="text-brand-navy border-brand-navy"
                    data-testid="setup-all-accounts"
                  >
                    {bulkSending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                    Setup All Accounts ({pendingAccountsCount})
                  </Button>
                )}
                <p className="text-sm text-navy-500">Total: {pagination.total} members</p>
              </div>
            </div>

            <Card className="border-0 shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-navy-50">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedMembers.length > 0 && selectedMembers.length === members.filter(m => !m.password_hash).length}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          className="rounded border-navy-300"
                          data-testid="select-all-members"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-navy-700">Name</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-navy-700">Email</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-navy-700">Employer</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-navy-700">Joined</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-navy-700">Welcome Email</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-navy-700">Account Status</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-navy-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {members.map((member) => (
                      <tr key={member.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          {!member.password_hash && (
                            <input
                              type="checkbox"
                              checked={selectedMembers.includes(member.id)}
                              onChange={(e) => handleSelectMember(member.id, e.target.checked)}
                              className="rounded border-navy-300"
                              data-testid={`select-member-${member.id}`}
                            />
                          )}
                        </td>
                        <td className="px-4 py-3 font-medium text-navy-900">{member.full_name}</td>
                        <td className="px-4 py-3 text-navy-600">{member.email}</td>
                        <td className="px-4 py-3 text-navy-600">{member.employer}</td>
                        <td className="px-4 py-3 text-sm text-navy-500">{formatDate(member.created_at)}</td>
                        <td className="px-4 py-3">
                          {member.welcome_email_sent ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              <Mail className="w-3 h-3" /> Sent
                            </span>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-brand-gold border-brand-gold hover:bg-gold-50"
                              onClick={() => handleSendWelcomeEmail(member.id, member.email)}
                              disabled={sendingEmail === member.id}
                              data-testid={`send-welcome-${member.id}`}
                            >
                              {sendingEmail === member.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Send className="w-3 h-3 mr-1" /> Send
                                </>
                              )}
                            </Button>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {member.password_hash ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              <Lock className="w-3 h-3" /> Active
                            </span>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-brand-navy border-brand-navy hover:bg-navy-50"
                              onClick={() => handleSendAccountSetup(member.id, member.email)}
                              disabled={setupAccountSending === member.id}
                              data-testid={`setup-account-${member.id}`}
                            >
                              {setupAccountSending === member.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Lock className="w-3 h-3 mr-1" /> Setup
                                </>
                              )}
                            </Button>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSelectedMember(member)}
                            data-testid={`view-member-${member.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => handleDelete('members', member.id)}
                            data-testid={`delete-member-${member.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {members.length === 0 && (
                <p className="text-center py-8 text-navy-500">No members found</p>
              )}
            </Card>

            {/* Pagination */}
            {pagination.total > pagination.limit && (
              <div className="flex justify-center gap-2">
                <Button
                  variant="outline"
                  disabled={pagination.skip === 0}
                  onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip - prev.limit }))}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="flex items-center px-4 text-sm text-navy-600">
                  Page {Math.floor(pagination.skip / pagination.limit) + 1} of {Math.ceil(pagination.total / pagination.limit)}
                </span>
                <Button
                  variant="outline"
                  disabled={pagination.skip + pagination.limit >= pagination.total}
                  onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Member Invitations Tab */}
        {activeTab === 'member-invitations' && (
          <div className="space-y-6" data-testid="member-invitations-tab">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-xl font-bold text-navy-900 mb-2">Member Profile Invitation</h2>
                <p className="text-navy-500 max-w-2xl">
                  Send a branded letter to existing members who haven&apos;t yet created their profile on the new website.
                  Track which emails are opened.
                </p>
              </div>
              <Button
                onClick={handleOpenInvitationPreview}
                disabled={invitationPreviewLoading}
                variant="outline"
                className="border-2 border-navy-200 text-navy-700 hover:bg-navy-50"
                data-testid="invitation-preview-btn"
              >
                {invitationPreviewLoading ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Loading…</>
                ) : (
                  <><Eye className="w-4 h-4 mr-2" /> Preview Letter</>
                )}
              </Button>
            </div>

            {/* Stats */}
            {invitationData && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="invitation-stats">
                <Card className="bg-white">
                  <CardContent className="p-5">
                    <p className="text-xs uppercase tracking-wider text-navy-500 mb-1">Total Sent</p>
                    <p className="text-3xl font-bold text-navy-900">{invitationData.sent}</p>
                  </CardContent>
                </Card>
                <Card className="bg-white">
                  <CardContent className="p-5">
                    <p className="text-xs uppercase tracking-wider text-navy-500 mb-1">Opened</p>
                    <p className="text-3xl font-bold text-brand-green">{invitationData.opened}</p>
                  </CardContent>
                </Card>
                <Card className="bg-white">
                  <CardContent className="p-5">
                    <p className="text-xs uppercase tracking-wider text-navy-500 mb-1">Open Rate</p>
                    <p className="text-3xl font-bold text-brand-gold">{invitationData.open_rate}%</p>
                  </CardContent>
                </Card>
                <Card className="bg-white">
                  <CardContent className="p-5">
                    <p className="text-xs uppercase tracking-wider text-navy-500 mb-1">Failed</p>
                    <p className="text-3xl font-bold text-red-600">{invitationData.failed}</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Recipient input + send */}
            <Card className="border-2 border-navy-100">
              <CardHeader>
                <CardTitle className="text-lg text-navy-900">Recipients</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="invitationRecipients">
                    Paste email addresses (one per line). Optional name fields supported:
                    <span className="font-mono text-xs ml-1 text-navy-500">email,FirstName,LastName</span>
                  </Label>
                  <Textarea
                    id="invitationRecipients"
                    data-testid="invitation-recipients-input"
                    value={invitationRecipientsText}
                    onChange={(e) => setInvitationRecipientsText(e.target.value)}
                    placeholder={'jane@example.com\njohn@example.com,John\nmarcus@example.com,Marcus,Lee'}
                    rows={8}
                    className="mt-2 font-mono text-sm"
                  />
                  <p className="text-xs text-navy-500 mt-2">
                    Each recipient gets a personalized greeting (if name is provided) and a unique tracking
                    pixel so opens can be measured individually.
                  </p>
                </div>
                <div className="flex gap-3 flex-wrap">
                  <Button
                    onClick={handleSendInvitations}
                    disabled={invitationSending || !invitationRecipientsText.trim()}
                    className="bg-brand-navy hover:bg-navy-700 text-white"
                    data-testid="invitation-send-btn"
                  >
                    {invitationSending ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                    ) : (
                      <><Send className="w-4 h-4 mr-2" /> Send Invitations</>
                    )}
                  </Button>
                  <Button
                    onClick={fetchInvitationData}
                    variant="outline"
                    className="border-2 border-navy-200 text-navy-700 hover:bg-navy-50"
                    data-testid="invitation-refresh-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Tracking table */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg text-navy-900">
                  Sent Invitations {invitationData ? `(${invitationData.total})` : ''}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {!invitationData || invitationData.recipients.length === 0 ? (
                  <p className="p-6 text-navy-500 text-sm">
                    No invitations sent yet. Paste recipient emails above and click <em>Send Invitations</em>.
                  </p>
                ) : (
                  <div className="overflow-x-auto" data-testid="invitation-recipients-table">
                    <table className="min-w-full text-sm">
                      <thead className="bg-navy-50 text-navy-700">
                        <tr>
                          <th className="text-left px-4 py-3 font-semibold">Email</th>
                          <th className="text-left px-4 py-3 font-semibold">Name</th>
                          <th className="text-left px-4 py-3 font-semibold">Sent</th>
                          <th className="text-left px-4 py-3 font-semibold">Status</th>
                          <th className="text-left px-4 py-3 font-semibold">Opened</th>
                          <th className="text-left px-4 py-3 font-semibold">Opens</th>
                          <th className="text-right px-4 py-3 font-semibold">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invitationData.recipients.map((r) => {
                          const sentAt = r.sent_at ? new Date(r.sent_at) : null;
                          const openedAt = r.opened_at ? new Date(r.opened_at) : null;
                          return (
                            <tr key={r.id} className="border-t border-navy-50 hover:bg-navy-50/40">
                              <td className="px-4 py-3 text-navy-900 font-medium">{r.email}</td>
                              <td className="px-4 py-3 text-navy-600">
                                {[r.first_name, r.last_name].filter(Boolean).join(' ') || '—'}
                              </td>
                              <td className="px-4 py-3 text-navy-600">
                                {sentAt ? sentAt.toLocaleString() : '—'}
                              </td>
                              <td className="px-4 py-3">
                                {r.status === 'sent' ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
                                    <Check className="w-3 h-3" /> Sent
                                  </span>
                                ) : r.status === 'failed' ? (
                                  <span
                                    title={r.error_message || ''}
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-100 text-red-700 text-xs font-semibold"
                                  >
                                    <X className="w-3 h-3" /> Failed
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">
                                    Pending
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-navy-600">
                                {openedAt ? (
                                  <span className="text-brand-green font-medium">
                                    {openedAt.toLocaleString()}
                                  </span>
                                ) : (
                                  <span className="text-navy-400">Not opened</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-navy-700 font-semibold">{r.open_count || 0}</td>
                              <td className="px-4 py-3 text-right">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDeleteInvitationRecord(r.id)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`invitation-delete-${r.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Conference Registrations Tab */}
        {activeTab === 'email-outreach' && (
          <OutreachComposer getPassword={() => sessionStorage.getItem(STORAGE_KEY) || ''} />
        )}

        {activeTab === 'conference-registrations' && (
          <div className="space-y-6" data-testid="conference-registrations-tab">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-xl font-bold text-navy-900 mb-2">2026 Conference Registrations</h2>
                <p className="text-navy-500 max-w-2xl">
                  Attendees who registered for the 4th Annual Citywide Conference (Oct 12, 2026 · Aspen Institute).
                </p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  onClick={fetchConfRegistrations}
                  variant="outline"
                  className="border-2 border-navy-200 text-navy-700 hover:bg-navy-50"
                  data-testid="conf-reg-refresh"
                >
                  <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                </Button>
                <ConferenceNotifier
                  registrations={confRegData?.registrations || []}
                  getPassword={() => sessionStorage.getItem(STORAGE_KEY) || ''}
                  onSent={fetchConfRegistrations}
                />
                <Button
                  onClick={() => handleBulkResendConfRegistration(true)}
                  disabled={confRegBulkSending || !confRegData || confRegData.total === 0}
                  variant="outline"
                  className="border-2 border-brand-green text-brand-green hover:bg-green-50"
                  data-testid="conf-reg-bulk-unsent"
                >
                  {confRegBulkSending ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" /> Send to Unconfirmed</>
                  )}
                </Button>
                <Button
                  onClick={() => handleBulkResendConfRegistration(false)}
                  disabled={confRegBulkSending || !confRegData || confRegData.total === 0}
                  className="bg-brand-green hover:bg-green-700 text-white"
                  data-testid="conf-reg-bulk-all"
                >
                  {confRegBulkSending ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" /> Send to All</>
                  )}
                </Button>
                <Button
                  onClick={handleExportConfRegistrations}
                  disabled={confRegExporting || !confRegData || confRegData.total === 0}
                  className="bg-brand-navy hover:bg-navy-700 text-white"
                  data-testid="conf-reg-export"
                >
                  {confRegExporting ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Exporting…</>
                  ) : (
                    <><Download className="w-4 h-4 mr-2" /> Export CSV</>
                  )}
                </Button>
              </div>
            </div>

            {confRegData && (
              <>
                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-brand-navy to-navy-700 text-white">
                    <CardContent className="p-5">
                      <p className="text-xs uppercase tracking-wider text-navy-200 mb-1">Total Registered</p>
                      <p className="text-4xl font-bold">{confRegData.total}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-5">
                      <p className="text-xs uppercase tracking-wider text-navy-500 mb-2">Roles Breakdown</p>
                      {Object.keys(confRegData.roles_breakdown || {}).length === 0 ? (
                        <p className="text-sm text-navy-400">No data yet</p>
                      ) : (
                        <ul className="text-xs text-navy-700 space-y-0.5">
                          {Object.entries(confRegData.roles_breakdown).slice(0, 4).map(([k, v]) => (
                            <li key={k} className="flex justify-between gap-2"><span className="truncate">{k}</span><span className="font-semibold">{v}</span></li>
                          ))}
                        </ul>
                      )}
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-5">
                      <p className="text-xs uppercase tracking-wider text-navy-500 mb-2">Dietary Breakdown</p>
                      {Object.keys(confRegData.dietary_breakdown || {}).length === 0 ? (
                        <p className="text-sm text-navy-400">No data yet</p>
                      ) : (
                        <ul className="text-xs text-navy-700 space-y-0.5">
                          {Object.entries(confRegData.dietary_breakdown).slice(0, 4).map(([k, v]) => (
                            <li key={k} className="flex justify-between gap-2"><span className="truncate">{k}</span><span className="font-semibold">{v}</span></li>
                          ))}
                        </ul>
                      )}
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-5">
                      <p className="text-xs uppercase tracking-wider text-navy-500 mb-2">Experience</p>
                      {Object.keys(confRegData.experience_breakdown || {}).length === 0 ? (
                        <p className="text-sm text-navy-400">No data yet</p>
                      ) : (
                        <ul className="text-xs text-navy-700 space-y-0.5">
                          {Object.entries(confRegData.experience_breakdown).map(([k, v]) => (
                            <li key={k} className="flex justify-between gap-2"><span className="truncate">{k}</span><span className="font-semibold">{v}</span></li>
                          ))}
                        </ul>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Search */}
                <div className="flex gap-2 max-w-md">
                  <div className="relative flex-1">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-navy-400" />
                    <Input
                      placeholder="Search by name, email, organization…"
                      value={confRegSearch}
                      onChange={(e) => setConfRegSearch(e.target.value)}
                      className="pl-9"
                      data-testid="conf-reg-search"
                    />
                  </div>
                </div>

                {/* Table */}
                <Card>
                  <CardContent className="p-0">
                    {confRegData.registrations.length === 0 ? (
                      <p className="p-6 text-navy-500 text-sm">No registrations yet.</p>
                    ) : (
                      <div className="overflow-x-auto" data-testid="conf-reg-table">
                        <table className="min-w-full text-sm">
                          <thead className="bg-navy-50 text-navy-700">
                            <tr>
                              <th className="text-left px-4 py-3 font-semibold">Name</th>
                              <th className="text-left px-4 py-3 font-semibold">Email</th>
                              <th className="text-left px-4 py-3 font-semibold">Organization</th>
                              <th className="text-left px-4 py-3 font-semibold">City, State</th>
                              <th className="text-left px-4 py-3 font-semibold">Registered</th>
                              <th className="text-left px-4 py-3 font-semibold">Email Sent</th>
                              <th className="text-right px-4 py-3 font-semibold">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {confRegData.registrations
                              .filter((r) => {
                                if (!confRegSearch.trim()) return true;
                                const q = confRegSearch.toLowerCase();
                                return (
                                  (r.first_name || '').toLowerCase().includes(q) ||
                                  (r.last_name || '').toLowerCase().includes(q) ||
                                  (r.email || '').toLowerCase().includes(q) ||
                                  (r.organization || '').toLowerCase().includes(q)
                                );
                              })
                              .map((r) => {
                                const created = r.created_at ? new Date(r.created_at) : null;
                                return (
                                  <tr key={r.id} className="border-t border-navy-50 hover:bg-navy-50/40">
                                    <td className="px-4 py-3 text-navy-900 font-medium">
                                      {[r.first_name, r.last_name].filter(Boolean).join(' ')}
                                      {r.preferred_name && (
                                        <span className="ml-2 text-xs text-navy-500">({r.preferred_name})</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 text-navy-600">{r.email}</td>
                                    <td className="px-4 py-3 text-navy-600 max-w-[220px] truncate">{r.organization || '—'}</td>
                                    <td className="px-4 py-3 text-navy-600">
                                      {[r.city, r.state].filter(Boolean).join(', ') || '—'}
                                    </td>
                                    <td className="px-4 py-3 text-navy-600 whitespace-nowrap">
                                      {created ? created.toLocaleDateString() : '—'}
                                    </td>
                                    <td className="px-4 py-3">
                                      {r.confirmation_email_sent ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
                                          <Check className="w-3 h-3" /> Sent
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">
                                          Not sent
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 text-right whitespace-nowrap">
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => setConfRegSelected(r)}
                                        className="text-navy-600 hover:text-navy-900 hover:bg-navy-50"
                                        data-testid={`conf-reg-view-${r.id}`}
                                      >
                                        <Eye className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleResendConfRegistration(r.id)}
                                        disabled={confRegResendingId === r.id}
                                        className="text-brand-green hover:text-green-700 hover:bg-green-50"
                                        title="Resend confirmation email"
                                        data-testid={`conf-reg-resend-${r.id}`}
                                      >
                                        {confRegResendingId === r.id ? (
                                          <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                          <Send className="w-4 h-4" />
                                        )}
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleDeleteConfRegistration(r.id)}
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                        data-testid={`conf-reg-delete-${r.id}`}
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        )}

        {/* Conference Supporters Tab */}
        {activeTab === 'conference' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <form onSubmit={handleSearch} className="flex gap-2 w-full sm:w-auto">
                <Input
                  placeholder="Search conference supporters..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full sm:w-64"
                  data-testid="admin-search-conference"
                />
                <Button type="submit" variant="outline">
                  <Search className="w-4 h-4" />
                </Button>
                <Button type="button" variant="outline" onClick={() => { setSearchTerm(''); fetchConferenceMembers(); }}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </form>
              <div className="flex items-center gap-3 flex-wrap">
                <p className="text-sm text-navy-500">Total: {pagination.total} conference supporters</p>
                <Button
                  onClick={handleExportConferenceSupporters}
                  disabled={exporting || pagination.total === 0}
                  className="bg-brand-navy hover:bg-navy-700 text-white"
                  data-testid="admin-export-conference-supporters"
                >
                  {exporting ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Exporting…</>
                  ) : (
                    <><Download className="w-4 h-4 mr-2" /> Download CSV</>
                  )}
                </Button>
              </div>
            </div>

            <Card className="border-0 shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gold-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-navy-700">Name</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-navy-700">Email</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-navy-700">Employer</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-navy-700">Serve on Committee</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-navy-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {conferenceMembers.map((member) => (
                      <tr key={member.id} className="hover:bg-gold-50/50">
                        <td className="px-4 py-3 font-medium text-navy-900">{member.full_name}</td>
                        <td className="px-4 py-3 text-navy-600">{member.email}</td>
                        <td className="px-4 py-3 text-navy-600">{member.employer}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            member.willing_serve_committee === 'Yes' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {member.willing_serve_committee || 'Not specified'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSelectedMember(member)}
                            data-testid={`view-conference-member-${member.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {conferenceMembers.length === 0 && (
                <p className="text-center py-8 text-navy-500">No conference supporters found</p>
              )}
            </Card>

            {/* Pagination */}
            {pagination.total > pagination.limit && (
              <div className="flex justify-center gap-2">
                <Button
                  variant="outline"
                  disabled={pagination.skip === 0}
                  onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip - prev.limit }))}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="flex items-center px-4 text-sm text-navy-600">
                  Page {Math.floor(pagination.skip / pagination.limit) + 1} of {Math.ceil(pagination.total / pagination.limit)}
                </span>
                <Button
                  variant="outline"
                  disabled={pagination.skip + pagination.limit >= pagination.total}
                  onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <form onSubmit={handleSearch} className="flex gap-2 w-full sm:w-auto">
                <Input
                  placeholder="Search contacts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full sm:w-64"
                  data-testid="admin-search-contacts"
                />
                <Button type="submit" variant="outline">
                  <Search className="w-4 h-4" />
                </Button>
                <Button type="button" variant="outline" onClick={() => { setSearchTerm(''); fetchContacts(); }}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </form>
              <p className="text-sm text-navy-500">Total: {pagination.total} messages</p>
            </div>

            <div className="grid gap-4">
              {contacts.map((contact) => (
                <Card key={contact.id} className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-bold text-navy-900">{contact.name}</h3>
                          <span className="text-sm text-navy-500">•</span>
                          <a href={`mailto:${contact.email}`} className="text-brand-green hover:underline text-sm">
                            {contact.email}
                          </a>
                        </div>
                        {contact.subject && (
                          <p className="text-sm font-medium text-navy-700 mb-2">Subject: {contact.subject}</p>
                        )}
                        <p className="text-navy-600 whitespace-pre-wrap">{contact.message}</p>
                        <p className="text-xs text-navy-400 mt-3">{formatDate(contact.created_at)}</p>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.location.href = `mailto:${contact.email}?subject=Re: ${contact.subject || 'Your inquiry'}`}
                        >
                          Reply
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDelete('contacts', contact.id)}
                          data-testid={`delete-contact-${contact.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {contacts.length === 0 && (
              <Card className="border-0 shadow-lg">
                <CardContent className="p-8 text-center">
                  <MessageSquare className="w-12 h-12 text-navy-300 mx-auto mb-4" />
                  <p className="text-navy-500">No contact messages found</p>
                </CardContent>
              </Card>
            )}

            {/* Pagination */}
            {pagination.total > pagination.limit && (
              <div className="flex justify-center gap-2">
                <Button
                  variant="outline"
                  disabled={pagination.skip === 0}
                  onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip - prev.limit }))}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="flex items-center px-4 text-sm text-navy-600">
                  Page {Math.floor(pagination.skip / pagination.limit) + 1} of {Math.ceil(pagination.total / pagination.limit)}
                </span>
                <Button
                  variant="outline"
                  disabled={pagination.skip + pagination.limit >= pagination.total}
                  onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Newsletter Tab */}
        {activeTab === 'newsletter' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <form onSubmit={handleSearch} className="flex gap-2 w-full sm:w-auto">
                <Input
                  placeholder="Search subscribers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full sm:w-64"
                  data-testid="admin-search-newsletter"
                />
                <Button type="submit" variant="outline">
                  <Search className="w-4 h-4" />
                </Button>
                <Button type="button" variant="outline" onClick={() => { setSearchTerm(''); fetchSubscribers(); }}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </form>
              <p className="text-sm text-navy-500">Total: {pagination.total} subscribers</p>
            </div>

            <Card className="border-0 shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-navy-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-navy-700">Email</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-navy-700">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-navy-700">Subscribed</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-navy-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {subscribers.map((sub) => (
                      <tr key={sub.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-navy-900">{sub.email}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            sub.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {sub.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-navy-500">{formatDate(sub.subscribed_at)}</td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => handleDelete('newsletter', sub.id)}
                            data-testid={`delete-subscriber-${sub.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {subscribers.length === 0 && (
                <p className="text-center py-8 text-navy-500">No subscribers found</p>
              )}
            </Card>

            {/* Pagination */}
            {pagination.total > pagination.limit && (
              <div className="flex justify-center gap-2">
                <Button
                  variant="outline"
                  disabled={pagination.skip === 0}
                  onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip - prev.limit }))}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="flex items-center px-4 text-sm text-navy-600">
                  Page {Math.floor(pagination.skip / pagination.limit) + 1} of {Math.ceil(pagination.total / pagination.limit)}
                </span>
                <Button
                  variant="outline"
                  disabled={pagination.skip + pagination.limit >= pagination.total}
                  onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Events Tab */}
        {activeTab === 'events' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-navy-900">Event Management</h2>
              <Button 
                onClick={() => { setShowEventForm(true); setEditingEvent(null); }}
                className="bg-brand-green hover:bg-green-700"
                data-testid="create-event-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Create Event
              </Button>
            </div>

            {/* Event Form Modal */}
            {showEventForm && (
              <Card className="border-2 border-brand-gold">
                <CardHeader>
                  <CardTitle>{editingEvent ? 'Edit Event' : 'Create New Event'}</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleCreateEvent} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Event Title *</Label>
                        <Input
                          value={eventForm.title}
                          onChange={(e) => setEventForm(prev => ({ ...prev, title: e.target.value }))}
                          required
                          data-testid="event-title-input"
                        />
                      </div>
                      <div>
                        <Label>Date *</Label>
                        <Input
                          type="date"
                          value={eventForm.eventDate}
                          onChange={(e) => setEventForm(prev => ({ ...prev, eventDate: e.target.value }))}
                          required
                          data-testid="event-date-input"
                        />
                      </div>
                      <div>
                        <Label>Start Time *</Label>
                        <Input
                          type="time"
                          value={eventForm.startTime}
                          onChange={(e) => setEventForm(prev => ({ ...prev, startTime: e.target.value }))}
                          required
                        />
                      </div>
                      <div>
                        <Label>End Time</Label>
                        <Input
                          type="time"
                          value={eventForm.endTime}
                          onChange={(e) => setEventForm(prev => ({ ...prev, endTime: e.target.value }))}
                        />
                      </div>
                      <div>
                        <Label>Location</Label>
                        <Input
                          value={eventForm.location}
                          onChange={(e) => setEventForm(prev => ({ ...prev, location: e.target.value }))}
                          placeholder="e.g., Community Center"
                        />
                      </div>
                      <div>
                        <Label>Address</Label>
                        <Input
                          value={eventForm.locationAddress}
                          onChange={(e) => setEventForm(prev => ({ ...prev, locationAddress: e.target.value }))}
                          placeholder="Full address"
                        />
                      </div>
                      <div>
                        <Label>Max Attendees</Label>
                        <Input
                          type="number"
                          value={eventForm.maxAttendees}
                          onChange={(e) => setEventForm(prev => ({ ...prev, maxAttendees: e.target.value }))}
                          placeholder="Leave empty for unlimited"
                        />
                      </div>
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={eventForm.isVirtual}
                            onChange={(e) => setEventForm(prev => ({ ...prev, isVirtual: e.target.checked }))}
                          />
                          Virtual Event
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={eventForm.rsvpEnabled}
                            onChange={(e) => setEventForm(prev => ({ ...prev, rsvpEnabled: e.target.checked }))}
                          />
                          Enable RSVP
                        </label>
                      </div>
                    </div>
                    
                    {eventForm.isVirtual && (
                      <div>
                        <Label>Virtual Meeting Link</Label>
                        <Input
                          value={eventForm.virtualLink}
                          onChange={(e) => setEventForm(prev => ({ ...prev, virtualLink: e.target.value }))}
                          placeholder="https://zoom.us/..."
                        />
                      </div>
                    )}
                    
                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={eventForm.description}
                        onChange={(e) => setEventForm(prev => ({ ...prev, description: e.target.value }))}
                        rows={3}
                      />
                    </div>
                    
                    <div className="flex gap-2 justify-end">
                      <Button type="button" variant="outline" onClick={() => setShowEventForm(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-brand-green hover:bg-green-700">
                        {editingEvent ? 'Update Event' : 'Create Event'}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Events List */}
            <div className="grid gap-4">
              {events.length > 0 ? events.map(event => (
                <Card key={event.id} className="overflow-hidden">
                  <div className="flex">
                    <div className="bg-brand-navy text-white p-4 flex flex-col items-center justify-center min-w-[80px]">
                      <span className="text-brand-gold text-xs font-bold">
                        {new Date(event.event_date).toLocaleDateString('en-US', { month: 'short' }).toUpperCase()}
                      </span>
                      <span className="text-2xl font-bold">{new Date(event.event_date).getDate()}</span>
                    </div>
                    <CardContent className="flex-1 p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-bold text-navy-900">{event.title}</h3>
                          <div className="flex gap-4 text-sm text-navy-500 mt-1">
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" /> {event.start_time}
                            </span>
                            {event.location && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" /> {event.location}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Users className="w-4 h-4" /> {event.rsvp_count || 0} RSVPs
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => handleEditEvent(event)}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-500" onClick={() => handleDeleteEvent(event.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </div>
                </Card>
              )) : (
                <Card className="p-8 text-center">
                  <Calendar className="w-12 h-12 text-navy-300 mx-auto mb-4" />
                  <p className="text-navy-500">No events created yet</p>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* Email Templates Tab */}
        {activeTab === 'email-templates' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-navy-900 mb-2">Email Templates</h2>
              <p className="text-navy-500">Choose a template for welcome emails sent to new members. Click &quot;Preview&quot; to see how the email will look.</p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {emailTemplates.map(template => (
                <Card 
                  key={template.key} 
                  className={`transition-all ${
                    template.is_active ? 'ring-2 ring-brand-green shadow-lg' : 'hover:shadow-md'
                  }`}
                >
                  <div 
                    className="h-3 rounded-t-lg" 
                    style={{ backgroundColor: template.preview_color }}
                  />
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-bold text-navy-900">{template.name}</h3>
                      {template.is_active && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                          <Check className="w-3 h-3" /> Active
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-navy-500 mb-4">{template.description}</p>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => handlePreviewTemplate(template)}
                        data-testid={`preview-template-${template.key}`}
                      >
                        <Eye className="w-4 h-4 mr-1" /> Preview
                      </Button>
                      {!template.is_active && (
                        <Button 
                          size="sm" 
                          className="flex-1 bg-brand-navy hover:bg-navy-700"
                          onClick={() => handleActivateTemplate(template.key)}
                          data-testid={`activate-template-${template.key}`}
                        >
                          <Check className="w-4 h-4 mr-1" /> Use
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Account Setup Email Template */}
            <div className="mt-8">
              <h3 className="text-lg font-bold text-navy-900 mb-2">Account Setup Email</h3>
              <p className="text-navy-500 mb-4">This email is sent when you set up an account for an existing member. It includes their temporary password.</p>
              
              <Card className="hover:shadow-md transition-all">
                <div className="h-3 rounded-t-lg bg-brand-green" />
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-bold text-navy-900">Account Setup Email</h3>
                    <span className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                      <Lock className="w-3 h-3" /> System Template
                    </span>
                  </div>
                  <p className="text-sm text-navy-500 mb-4">Email sent to existing members with their login credentials (email + temporary password).</p>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handlePreviewAccountSetupTemplate()}
                    data-testid="preview-account-setup-template"
                  >
                    <Eye className="w-4 h-4 mr-1" /> Preview Template
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Theme Survey Tab */}
        {activeTab === 'theme-survey' && (
          <div className="space-y-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-xl font-bold text-navy-900 mb-2">Conference Theme Survey Results</h2>
                <p className="text-navy-500">View votes for the 2026 conference theme.</p>
              </div>
              <Button
                onClick={handleExportSurveyPdf}
                disabled={exportingSurveyPdf || !surveyResults || surveyResults.total_votes === 0}
                className="bg-brand-navy hover:bg-navy-700 text-white"
                data-testid="admin-download-survey-pdf"
              >
                {exportingSurveyPdf ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Preparing PDF…</>
                ) : (
                  <><Download className="w-4 h-4 mr-2" /> Download PDF</>
                )}
              </Button>
            </div>

            {surveyResults ? (
              <>
                {/* Summary Stats */}
                <div className="grid md:grid-cols-2 gap-6">
                  <Card className="bg-gradient-to-br from-brand-navy to-navy-700 text-white">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-navy-200 text-sm">Total Votes</p>
                          <p className="text-4xl font-bold">{surveyResults.total_votes}</p>
                        </div>
                        <Vote className="w-12 h-12 text-brand-gold opacity-80" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  {surveyResults.results[0] && (
                    <Card className="bg-gradient-to-br from-brand-green to-green-600 text-white">
                      <CardContent className="p-6">
                        <div>
                          <p className="text-green-100 text-sm">Leading Theme</p>
                          <p className="text-lg font-bold mt-1 line-clamp-2">{surveyResults.results[0].title}</p>
                          <p className="text-2xl font-bold mt-2">{surveyResults.results[0].percentage}% ({surveyResults.results[0].votes} votes)</p>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Results Breakdown */}
                <Card className="border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-lg">Theme Rankings</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {surveyResults.results.map((result, index) => (
                      <div key={result.theme_id} className="border-b border-navy-100 pb-6 last:border-0 last:pb-0">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-start gap-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                              index === 0 ? 'bg-brand-green' : 
                              index === 1 ? 'bg-brand-gold text-navy-900' : 
                              index === 2 ? 'bg-blue-500' : 'bg-navy-400'
                            }`}>
                              {index + 1}
                            </div>
                            <div>
                              <h3 className="font-bold text-navy-900">{result.title}</h3>
                              <p className="text-sm text-navy-500 italic">{result.subtext}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-brand-green">{result.percentage}%</p>
                            <p className="text-sm text-navy-500">{result.votes} votes</p>
                          </div>
                        </div>
                        
                        {/* Progress Bar */}
                        <div className="w-full bg-navy-100 rounded-full h-4 mb-3">
                          <div 
                            className={`h-4 rounded-full transition-all duration-500 ${
                              index === 0 ? 'bg-brand-green' : 
                              index === 1 ? 'bg-brand-gold' : 
                              index === 2 ? 'bg-blue-500' : 'bg-navy-400'
                            }`}
                            style={{ width: `${result.percentage}%` }}
                          />
                        </div>
                        
                        {/* Breakout Strands */}
                        <div className="mt-3 pl-14">
                          <p className="text-xs font-semibold text-navy-600 mb-1">Breakout Strands:</p>
                          <ul className="text-sm text-navy-500 space-y-1">
                            {result.strands.map((strand, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <span className="text-brand-green">•</span>
                                {strand}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Recent Votes */}
                {surveyResults.recent_votes && surveyResults.recent_votes.length > 0 && (
                  <Card className="border-0 shadow-lg">
                    <CardHeader>
                      <CardTitle className="text-lg">Recent Votes</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-navy-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-sm font-medium text-navy-700">Voter</th>
                              <th className="px-4 py-3 text-left text-sm font-medium text-navy-700">Email</th>
                              <th className="px-4 py-3 text-left text-sm font-medium text-navy-700">Theme</th>
                              <th className="px-4 py-3 text-left text-sm font-medium text-navy-700">Date</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {surveyResults.recent_votes.map((vote) => (
                              <tr key={vote.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-navy-900">{vote.voter_name || 'Anonymous'}</td>
                                <td className="px-4 py-3 text-navy-600">{vote.voter_email || '-'}</td>
                                <td className="px-4 py-3 text-navy-600 max-w-xs truncate">{vote.theme_title}</td>
                                <td className="px-4 py-3 text-sm text-navy-500">
                                  {new Date(vote.created_at).toLocaleDateString()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Card className="border-0 shadow-lg">
                <CardContent className="p-12 text-center">
                  <Vote className="w-16 h-16 text-navy-300 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-navy-900 mb-2">No Votes Yet</h3>
                  <p className="text-navy-500">Votes will appear here once participants submit their choices.</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Member Detail Modal */}
      {selectedMember && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-brand-navy text-white p-6 rounded-t-2xl flex justify-between items-center">
              <h2 className="text-xl font-bold">Member Details</h2>
              <div className="flex items-center gap-3">
                {!selectedMember.welcome_email_sent && (
                  <Button
                    size="sm"
                    className="bg-brand-gold hover:bg-gold-600 text-navy-900"
                    onClick={() => handleSendWelcomeEmail(selectedMember.id, selectedMember.email)}
                    disabled={sendingEmail === selectedMember.id}
                    data-testid="modal-send-welcome-email"
                  >
                    {sendingEmail === selectedMember.id ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-1" />
                    ) : (
                      <Send className="w-4 h-4 mr-1" />
                    )}
                    Send Welcome Email
                  </Button>
                )}
                {selectedMember.welcome_email_sent && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-100">
                    <Mail className="w-3 h-3" /> Welcome Email Sent
                  </span>
                )}
                <button onClick={() => setSelectedMember(null)} className="text-white/80 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Personal Info */}
              <div>
                <h3 className="text-lg font-bold text-navy-900 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-brand-green" />
                  Personal Information
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-navy-500">Full Name</p>
                    <p className="font-medium text-navy-900">{selectedMember.full_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-navy-500">Email</p>
                    <p className="font-medium text-navy-900">{selectedMember.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-navy-500">Phone</p>
                    <p className="font-medium text-navy-900">{selectedMember.phone || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-navy-500">Date of Birth</p>
                    <p className="font-medium text-navy-900">{selectedMember.date_of_birth || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-navy-500">Marital Status</p>
                    <p className="font-medium text-navy-900">{selectedMember.marital_status || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-navy-500">Has Children</p>
                    <p className="font-medium text-navy-900">{selectedMember.has_children || 'N/A'}</p>
                  </div>
                </div>
                {selectedMember.street_address && (
                  <div className="mt-4">
                    <p className="text-sm text-navy-500">Address</p>
                    <p className="font-medium text-navy-900">
                      {selectedMember.street_address}, {selectedMember.city}, {selectedMember.state} {selectedMember.zip_code}
                    </p>
                  </div>
                )}
              </div>

              {/* Professional Info */}
              <div>
                <h3 className="text-lg font-bold text-navy-900 mb-4 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-brand-gold" />
                  Professional Information
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-navy-500">Employer</p>
                    <p className="font-medium text-navy-900">{selectedMember.employer}</p>
                  </div>
                  <div>
                    <p className="text-sm text-navy-500">Job Title</p>
                    <p className="font-medium text-navy-900">{selectedMember.job_title}</p>
                  </div>
                  <div>
                    <p className="text-sm text-navy-500">Education Level</p>
                    <p className="font-medium text-navy-900">{selectedMember.education_level}</p>
                  </div>
                  <div>
                    <p className="text-sm text-navy-500">Years in ECE</p>
                    <p className="font-medium text-navy-900">{selectedMember.years_in_ece}</p>
                  </div>
                </div>
              </div>

              {/* Background */}
              <div>
                <h3 className="text-lg font-bold text-navy-900 mb-4 flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-brand-red" />
                  Background
                </h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-navy-500">What brought them to ECE</p>
                    <p className="font-medium text-navy-900">{selectedMember.what_brought_you_to_ece}</p>
                  </div>
                  <div>
                    <p className="text-sm text-navy-500">Skills & Talents</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {selectedMember.skills_and_talents?.map((skill, idx) => (
                        <span key={idx} className="px-3 py-1 bg-brand-green/10 text-brand-green rounded-full text-sm">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                  {selectedMember.hobbies_and_interests && (
                    <div>
                      <p className="text-sm text-navy-500">Hobbies & Interests</p>
                      <p className="font-medium text-navy-900">{selectedMember.hobbies_and_interests}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-navy-500">Willing to Mentor</p>
                    <p className="font-medium text-navy-900">{selectedMember.willing_to_mentor}</p>
                  </div>
                </div>
              </div>

              {/* Goals */}
              <div>
                <h3 className="text-lg font-bold text-navy-900 mb-4">Goals</h3>
                <p className="text-navy-900">{selectedMember.what_you_hope_to_gain}</p>
              </div>

              {/* Conference Support */}
              {(selectedMember.willing_support_conference || selectedMember.willing_serve_committee) && (
                <div className="p-4 bg-gold-50 rounded-xl">
                  <h3 className="text-lg font-bold text-navy-900 mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-brand-gold" />
                    Conference Support
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-navy-500">Willing to Support Conference</p>
                      <p className="font-medium text-navy-900">{selectedMember.willing_support_conference || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-navy-500">Willing to Serve on Committee</p>
                      <p className="font-medium text-navy-900">{selectedMember.willing_serve_committee || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Photo Consent */}
              <div className="p-4 bg-navy-50 rounded-xl">
                <h3 className="text-lg font-bold text-navy-900 mb-4 flex items-center gap-2">
                  <Camera className="w-5 h-5 text-brand-navy" />
                  Photo & Media Consent
                </h3>
                <div>
                  <p className="text-sm text-navy-500">Consents to photo/video use for DC Men in ECE promotion</p>
                  <p className={`font-medium ${
                    selectedMember.photo_consent === 'Yes' 
                      ? 'text-brand-green' 
                      : selectedMember.photo_consent === 'No' 
                        ? 'text-brand-red' 
                        : 'text-navy-900'
                  }`}>
                    {selectedMember.photo_consent || 'Not specified'}
                  </p>
                </div>
              </div>

              {/* Account Actions */}
              <div className="p-4 bg-blue-50 rounded-xl">
                <h3 className="text-lg font-bold text-navy-900 mb-4 flex items-center gap-2">
                  <Lock className="w-5 h-5 text-blue-600" />
                  Account Management
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-navy-500">Account Status</p>
                      <p className={`font-medium ${selectedMember.password_hash ? 'text-brand-green' : 'text-orange-500'}`}>
                        {selectedMember.password_hash ? 'Active (Has Password)' : 'Pending Setup'}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-blue-500 text-blue-600 hover:bg-blue-50"
                      onClick={() => handleAdminResetPassword(selectedMember.id)}
                      disabled={resettingPassword}
                      data-testid="admin-reset-password-btn"
                    >
                      {resettingPassword ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-1" />
                      ) : (
                        <Lock className="w-4 h-4 mr-1" />
                      )}
                      Reset Password
                    </Button>
                  </div>
                  
                  {resetPasswordResult && resetPasswordResult.email === selectedMember.email && (
                    <div className="p-4 bg-green-100 rounded-lg border border-green-300">
                      <p className="font-bold text-green-800 mb-2">Password Reset Successful!</p>
                      <div className="space-y-2">
                        <p className="text-sm text-green-700">
                          <strong>Email:</strong> {resetPasswordResult.email}
                        </p>
                        <p className="text-sm text-green-700">
                          <strong>Temporary Password:</strong>{' '}
                          <code className="bg-white px-2 py-1 rounded font-mono text-lg select-all">
                            {resetPasswordResult.temporary_password}
                          </code>
                        </p>
                        <p className="text-xs text-green-600 mt-2">
                          Share this password securely with the member. They should change it after logging in.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Meta */}
              <div className="pt-4 border-t">
                <p className="text-sm text-navy-400">
                  Registered: {formatDate(selectedMember.created_at)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Template Preview Modal */}
      {previewTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="sticky top-0 bg-brand-navy text-white p-6 rounded-t-2xl flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold">Email Template Preview</h2>
                <p className="text-white/70 text-sm">{previewTemplate.name}</p>
              </div>
              <div className="flex items-center gap-3">
                {!previewTemplate.is_active && (
                  <Button
                    size="sm"
                    className="bg-brand-gold hover:bg-gold-600 text-navy-900"
                    onClick={() => {
                      handleActivateTemplate(previewTemplate.key);
                      setPreviewTemplate(null);
                    }}
                    data-testid="modal-activate-template"
                  >
                    <Check className="w-4 h-4 mr-1" /> Use This Template
                  </Button>
                )}
                {previewTemplate.is_active && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-100">
                    <Check className="w-3 h-3" /> Currently Active
                  </span>
                )}
                <button 
                  onClick={() => setPreviewTemplate(null)} 
                  className="text-white/80 hover:text-white"
                  data-testid="close-preview-modal"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <div className="flex-1 overflow-auto bg-gray-100 p-4">
              {previewLoading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 animate-spin text-brand-navy" />
                </div>
              ) : (
                <div 
                  className="bg-white rounded-lg shadow-lg overflow-hidden"
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                  data-testid="template-preview-content"
                />
              )}
            </div>
          </div>
        </div>
      )}
      {/* Member Invitation Letter Preview Modal */}
      {invitationPreviewOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          data-testid="invitation-preview-modal"
        >
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="sticky top-0 bg-brand-navy text-white p-6 rounded-t-2xl flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold">Member Profile Invitation</h2>
                <p className="text-white/70 text-sm">
                  Subject: <span className="font-mono">{invitationPreviewSubject}</span>
                </p>
              </div>
              <button
                onClick={() => setInvitationPreviewOpen(false)}
                className="text-white/80 hover:text-white"
                data-testid="invitation-preview-close"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-auto bg-gray-100 p-4">
              <div
                className="bg-white rounded-lg shadow-lg overflow-hidden"
                dangerouslySetInnerHTML={{ __html: invitationPreviewHtml }}
                data-testid="invitation-preview-content"
              />
            </div>
          </div>
        </div>
      )}
      {/* Conference Registration Detail Modal */}
      {confRegSelected && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          data-testid="conf-reg-detail-modal"
        >
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="sticky top-0 bg-brand-navy text-white p-6 rounded-t-2xl flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold">
                  {[confRegSelected.first_name, confRegSelected.last_name].filter(Boolean).join(' ')}
                </h2>
                <p className="text-white/70 text-sm">{confRegSelected.email}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => handleResendConfRegistration(confRegSelected.id)}
                  disabled={confRegResendingId === confRegSelected.id}
                  size="sm"
                  className="bg-brand-green hover:bg-green-700 text-white"
                  data-testid="conf-reg-detail-resend"
                >
                  {confRegResendingId === confRegSelected.id ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" /> Resend Confirmation</>
                  )}
                </Button>
                <button
                  onClick={() => setConfRegSelected(null)}
                  className="text-white/80 hover:text-white"
                  data-testid="conf-reg-detail-close"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-6 space-y-6 text-sm text-navy-800">
              <DetailBlock title="Contact">
                <DetailRow label="Preferred Name" value={confRegSelected.preferred_name} />
                <DetailRow label="Mobile" value={confRegSelected.mobile_phone} />
                <DetailRow label="Organization" value={confRegSelected.organization} />
                <DetailRow label="Preferred Org (Badge)" value={confRegSelected.preferred_organization} />
                <DetailRow label="Job Title" value={confRegSelected.job_title} />
                <DetailRow label="Location" value={[confRegSelected.city, confRegSelected.state].filter(Boolean).join(', ')} />
              </DetailBlock>
              <DetailBlock title="Professional">
                <DetailRow label="Roles" value={(confRegSelected.roles || []).join(', ')} />
                <DetailRow label="Other Role" value={confRegSelected.roles_other} />
                <DetailRow label="Years of Experience" value={confRegSelected.years_experience} />
              </DetailBlock>
              <DetailBlock title="Conference Experience">
                <DetailRow label="Attended Before" value={confRegSelected.attended_before} />
                <DetailRow label="Previous Years" value={(confRegSelected.previous_years || []).join(', ')} />
                <DetailRow label="Other Previous Year" value={confRegSelected.previous_years_other} />
                <DetailRow label="How Heard" value={confRegSelected.how_heard} />
                <DetailRow label="How Heard (Other)" value={confRegSelected.how_heard_other} />
              </DetailBlock>
              <DetailBlock title="Language & Demographics">
                <DetailRow label="First Language" value={confRegSelected.first_language || confRegSelected.first_language_other} />
                <DetailRow label="Race / Ethnicity" value={(confRegSelected.race_ethnicity || []).join(', ')} />
                <DetailRow label="Race / Ethnicity (Other)" value={confRegSelected.race_ethnicity_other} />
              </DetailBlock>
              <DetailBlock title="Accessibility & Dietary">
                <DetailRow label="Accommodations Needed" value={confRegSelected.needs_accommodations} />
                <DetailRow label="Accommodation Details" value={confRegSelected.accommodations_description} />
                <DetailRow label="Dietary" value={(confRegSelected.dietary_restrictions || []).join(', ')} />
                <DetailRow label="Dietary (Other)" value={confRegSelected.dietary_other} />
              </DetailBlock>
              <DetailBlock title="Engagement">
                <DetailRow label="Looking Forward To" value={(confRegSelected.looking_forward_to || []).join(', ')} />
                <DetailRow label="Future Events Info" value={confRegSelected.future_events_info} />
                <DetailRow label="First ECE Conference" value={confRegSelected.first_ece_conference} />
                <DetailRow label="Mailing List Opt-In" value={confRegSelected.mailing_list_opt_in} />
                <DetailRow label="Sponsorship Info" value={confRegSelected.sponsorship_info} />
              </DetailBlock>
              <DetailBlock title="Consent">
                <DetailRow label="Media Consent" value={confRegSelected.media_consent === 'consent' ? 'Yes — consents to photo/video' : 'No — does not consent'} />
                <DetailRow label="Agreement" value={confRegSelected.agreement ? 'Agreed' : 'Not agreed'} />
                <DetailRow label="Confirmation Email" value={confRegSelected.confirmation_email_sent ? 'Sent' : 'Not sent'} />
                <DetailRow label="Registered" value={confRegSelected.created_at ? new Date(confRegSelected.created_at).toLocaleString() : ''} />
              </DetailBlock>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Small helpers for the detail modal
const DetailBlock = ({ title, children }) => (
  <div>
    <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider border-b border-navy-100 pb-2 mb-3">
      {title}
    </h3>
    <dl className="space-y-1.5">{children}</dl>
  </div>
);
const DetailRow = ({ label, value }) => (
  <div className="grid grid-cols-3 gap-3 text-sm">
    <dt className="text-navy-500 font-medium">{label}</dt>
    <dd className="col-span-2 text-navy-800 break-words">{value || <span className="text-navy-300">—</span>}</dd>
  </div>
);
